<template>
  <div class="w-full h-full">
    <div v-if="notAdmin" class="flex flex-col items-center justify-center gap-2 py-20 text-center">
      <ShieldAlert class="w-8 h-8 text-muted-foreground" />
      <p class="text-base font-medium">Billing is only available to admin users</p>
      <p class="text-sm text-muted-foreground">
        You don't have permission to view billing. Ask an admin on your account for access.
      </p>
    </div>
    <div v-else class="space-y-4">
      <div>
        <Card>
          <CardHeader>
            <CardTitle>Upcoming invoice</CardTitle>
            <CardDescription>{{ upcomingInvoiceSubText }}</CardDescription>
          </CardHeader>
          <CardContent>
            <div v-if="hasInvoice">
              <div
                v-for="(item, i) in lineItems"
                :key="i"
                class="flex items-center justify-between gap-4 py-3 border-b border-border"
              >
                <div class="min-w-0">
                  <p class="text-sm font-medium text-foreground">{{ item.label }}</p>
                  <p class="text-xs text-muted-foreground">{{ item.detail }}</p>
                </div>
                <p class="text-sm font-semibold tabular-nums whitespace-nowrap">£{{ item.amount }}</p>
              </div>

              <p v-if="!lineItems.length" class="py-6 text-center text-sm text-muted-foreground">
                No charges have accrued yet for this period.
              </p>

              <div class="flex items-center justify-between gap-4 pt-4">
                <p class="text-sm font-semibold">
                  Total <span class="font-normal text-muted-foreground">(excl. VAT)</span>
                </p>
                <p class="text-2xl font-bold tabular-nums">£{{ money(currentInvoiceData.invoiceTotal) }}</p>
              </div>
            </div>
            <div v-else class="py-10 text-center">
              <p class="text-sm font-medium text-foreground">No upcoming invoice</p>
              <p class="text-xs text-muted-foreground mt-1">
                There's no billing data for the current period yet.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
      <div>
        <Card>
          <CardHeader>
            <CardTitle>Historical invoices</CardTitle>
            <CardDescription>Check your emails for pdf invoice records, or contact our team for assistance</CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              :headers="headersTable1"
              :items="historicData"
              :loading="loading"
              :error="error"
              @retry="getHistoricInvoices(serverOptions.page - 1, serverOptions.rowsPerPage)"
              v-model:server-options="serverOptions"
              :server-items-length="serverItemsLength"
              :hide-footer="true"
            >
              <template #item-startDate="historicData">
                <span class="text-sm font-medium whitespace-nowrap">{{ niceDateTime(historicData.startDate) }}</span>
              </template>
              <template #item-endDate="historicData">
                <span class="text-sm font-medium whitespace-nowrap">{{ niceDateTime(historicData.endDate) }}</span>
              </template>
              <template #item-invoiceTotal="historicData">
                <div class="text-right tabular-nums">
                  <span class="text-sm font-medium whitespace-nowrap">
                    £ {{ parseFloat(historicData.invoiceTotal).toFixed(3) }}
                  </span>
                </div>
              </template>
            </DataTable>
          </CardContent>
        </Card>
      </div>
    </div>
  </div>
</template>

<script setup></script>

<script>
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@boogi/ui'
import { DateTime } from 'luxon'
import { DataTable } from '@boogi/ui'
import { ShieldAlert } from 'lucide-vue-next'

export default {
  name: 'manage-billing',
  components: {
    DataTable,
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    ShieldAlert,
  },
  props: {},

  data() {
    return {
      currentInvoiceData: '',
      historicData: [],
      serverOptions: {
        page: 1,
        rowsPerPage: 25,
      },
      serverItemsLength: undefined,
      showErrorModal: false,
      showSuccessModal: false,
      showFormModal: false,
      loading: true,
      error: false,
      notAdmin: false,
      headersTable1: [
        { text: 'Start date', value: 'startDate' },
        { text: 'End date', value: 'endDate' },
        { text: 'Total', value: 'invoiceTotal', align: 'right' },
      ],
      apiKeys: [],
    }
  },
  created() {
    this.getCurrentInvoice()
    this.getHistoricInvoices(0, 25)
  },
  methods: {
    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject).toLocaleString(DateTime.MED)
    },
    money(value, dp = 2) {
      const n = Number(value)
      return (Number.isFinite(n) ? n : 0).toFixed(dp)
    },
    async getCurrentInvoice() {
      try {
        const token = localStorage.getItem('token')
        this.loading = true

        const response = await this.$apiFetch(
          `${localStorage.getItem('apiUrl')}/private/api/client/invoice/current`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.status === 403 || response.status === 401) {
          this.notAdmin = true
        } else if (response.ok) {
          var object = await response.json()

          this.currentInvoiceData = object
        }
      } catch (error) {
        return
      }
    },
    async getHistoricInvoices(page, size) {
      const token = localStorage.getItem('token')
      this.loading = true
      this.error = false

      try {
        const response = await this.$apiFetch(
          `${localStorage.getItem('apiUrl')}/private/api/client/invoice/historic?page=${page}&size=${size}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.historicData = object.content
          this.serverItemsLength = object.totalElements
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
  },
  computed: {
    hasInvoice() {
      const d = this.currentInvoiceData
      return d && typeof d === 'object' && d.invoiceTotal != null
    },
    lineItems() {
      const d = this.currentInvoiceData
      if (!d || typeof d !== 'object') return []
      const items = []
      if (d.platformFeeReceivable > 0) {
        items.push({
          label: 'Platform fee',
          detail: `1 × £${this.money(d.platformFeeReceivable)}`,
          amount: this.money(d.platformFeeReceivable),
        })
      }
      if (d.totalTransactionProcessingReceivable > 0) {
        items.push({
          label: 'Transaction enrichment',
          detail: `${d.processedTransactions} × £${this.money(d.transactionProcessingFee, 3)}`,
          amount: this.money(d.totalTransactionProcessingReceivable, 3),
        })
      }
      if (d.totalOpenBankingConnectionReceivable > 0) {
        items.push({
          label: 'Bank connections',
          detail: `${d.openBankingConnection} × £${this.money(d.openBankingConnectionFee)}`,
          amount: this.money(d.totalOpenBankingConnectionReceivable),
        })
      }
      return items
    },
    upcomingInvoiceSubText() {
      if (!this.hasInvoice) {
        return 'No upcoming invoice data available'
      }
      const startDate = this.niceDateTime(this.currentInvoiceData.startDate)
      const endDate = this.niceDateTime(this.currentInvoiceData.endDate)

      return `For the period from ${startDate} to ${endDate}`
    },
  },
  mounted() {
    document.title = 'Dashboard | Billing'
  },
  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        this.getHistoricInvoices(val.page - 1, val.rowsPerPage)
      },
    },
  },
}
</script>
